/* Shared content for the new-look Femtoware site. */
import type { IconName } from './icons';

export interface Service {
  icon: IconName;
  name: string;
  tag: string;
  desc: string;
  points: string[];
  color: string;
  /** Link label on the Home service card. */
  cta: string;
  /** The product this offer is built on, shown beside it on the Services page. */
  image: string;
  imageAlt: string;
}

export interface CaseStudy {
  id: string;
  name: string;
  cat: string;
  industry: string;
  flagship?: boolean;
  tagline?: string;
  color: string;
  accent: string;
  problem: string;
  solution: string;
  metrics: [string, string][];
  tech: string[];
  /** Product image (public/ path): a real screenshot, or a placeholder scene photo. */
  image?: string;
  /** Brand suffix shown after the name, e.g. "by Femtoware". */
  byline?: string;
  /** Footnote shown with the metrics (sourcing for public claims). */
  note?: string;
  /** Product page — internal path (starts with '/') or external URL. */
  link?: { href: string; label: string };
}

export const SERVICES: Service[] = [
  {
    icon: 'code',
    name: 'Software built with NYASA',
    cta: 'How we build software',
    tag: 'Custom software, delivered faster',
    desc: 'Custom platforms and features built by our engineers with NYASA — AI agents plan, code and test each change, while our team approves every plan and reviews every pull request.',
    points: [
      'Web & mobile platforms',
      'New features in existing codebases',
      'Plan approval & code review',
      'Tests and security checks on every change',
    ],
    color: 'var(--indigo)',
    image: '/assets/img/products/nyasa-pipeline.jpg',
    imageAlt:
      'NYASA task view: a merged feature and every pipeline step, from ticket to pull request',
  },
  {
    icon: 'chat',
    name: 'AI agents for operations',
    cta: 'Explore AI agents',
    tag: 'Voice & chat agents',
    desc: 'AI agents that answer calls and messages, take bookings and orders, and hand over to your team — built on the technology behind Table Finch and connected to the systems you already use.',
    points: [
      'AI receptionist & call routing',
      'Bookings, orders & reminders',
      'CRM & management-system integrations',
      'Human hand-over with full context',
    ],
    color: 'var(--blue)',
    image: '/assets/img/table-finch/clinic.jpg',
    imageAlt: 'A front desk with an AI agent answering calls',
  },
  {
    icon: 'chart',
    name: 'Optimisation & data',
    cta: 'Explore optimisation',
    tag: 'Better decisions from your data',
    desc: 'Routing, scheduling and forecasting built on the engineering behind Compasso — plus the data pipelines and retrieval that ground AI in your real data.',
    points: [
      'Route & schedule optimisation',
      'Forecasting & analytics',
      'Data pipelines & retrieval (RAG)',
      'AI strategy & roadmaps',
    ],
    color: 'var(--green)',
    image: '/assets/img/products/compasso-routes.jpg',
    imageAlt: 'Compasso planning delivery routes across Bangalore',
  },
];

export const CASES: CaseStudy[] = [
  {
    id: 'nyasa',
    name: 'NYASA',
    byline: 'by Femtoware',
    cat: 'AI Platform',
    industry: 'Software Delivery',
    flagship: true,
    tagline: 'AI agents that turn tickets into reviewed pull requests',
    color: '#00A0F7',
    accent: '#2fd11f',
    problem:
      'Shipping software means slow, manual cycles — scaffolding, tests, reviews and CI before real product value lands.',
    solution:
      'Assign a ticket and NYASA’s agents plan the change, wait for a human to approve the plan, then write the code, run the tests and open a pull request on your GitHub repo — with per-person access levels and AI budgets set by your admin. We use it every day to build our own products.',
    metrics: [
      ['Plan → PR', 'Coded and tested by agents'],
      ['Every plan', 'Approved by a person'],
      ['Per person', 'Access and AI budgets'],
    ],
    tech: ['AI coding agents', 'Multi-agent orchestration', 'GitHub', 'CI/CD', 'Role-based access'],
    image: '/assets/img/products/nyasa-pipeline.jpg',
    link: { href: '/nyasa', label: 'Explore NYASA' },
  },
  {
    id: 'compasso',
    name: 'Compasso',
    cat: 'Route Optimisation',
    industry: 'Transport & Logistics',
    tagline: 'Optimal vehicle routes, in seconds — on a stack you own',
    color: '#09AA00',
    accent: '#00A0F7',
    problem:
      'Planning daily transport — employee shifts, deliveries, field visits — by hand or on metered map APIs is slow, costly and hard to audit.',
    solution:
      'Give Compasso your stops, vehicles and rules. It returns road-true, constraint-checked routes with live-traffic ETAs in seconds — running on self-hosted infrastructure, so costs stay flat and your location data stays yours. Safety (night-trip escort seats) and fairness (farthest rider boarded first) are built into every route.',
    metrics: [
      ['Up to 90%', 'Lower routing costs'],
      ['~20%', 'Shorter routes than manual'],
      ['5 sec', 'To plan 300 riders'],
    ],
    tech: ['Self-hosted routing', 'Constraint optimisation', 'Live-traffic ETAs', 'API-first'],
    image: '/assets/img/products/compasso-routes.jpg',
    note: 'Figures from a client deployment (≈$5,000/mo → under $500) and internal benchmarks, Jun–Jul 2026; methodology available on request.',
    link: { href: '/compasso', label: 'Explore Compasso' },
  },
  {
    id: 'tablefinch',
    name: 'Table Finch',
    cat: 'Restaurant Platform',
    industry: 'Restaurants · Bars · Pizzerias',
    tagline: 'The all-in-one restaurant operating system with a built-in AI receptionist',
    color: '#180059',
    accent: '#00A0F7',
    problem:
      'Restaurants juggle separate tools for bookings, orders, the till and stock — while the phone keeps ringing at the busiest moments.',
    solution:
      'Fourteen tools in one platform — reservations and a live floor plan, waiter app, kitchen display, POS, QR menu, inventory, staff and analytics — with an AI receptionist that answers the phone and WhatsApp and books straight onto the floor plan.',
    metrics: [
      ['14', 'Tools in one platform'],
      ['24/7', 'AI phone & WhatsApp receptionist'],
      ['US · Italy', 'Markets'],
    ],
    tech: ['AI receptionist', 'Restaurant modules', 'Real-time sync', 'Fiscal & payments'],
    image: '/assets/img/table-finch/table.jpg',
    link: { href: '/table-finch', label: 'Explore Table Finch' },
  },
];

export const PROCESS: [string, string, IconName][] = [
  [
    'Discover',
    'We learn how your business runs and find where AI can take work off your team.',
    'search',
  ],
  ['Prioritize', 'We pick the few use cases worth building first — by value and effort.', 'chart'],
  [
    'Build',
    'We engineer with NYASA — AI agents write and test the code, our engineers review every change.',
    'code',
  ],
  ['Launch', 'We go live on secure infrastructure and help your team start using it.', 'rocket'],
  ['Improve', 'We track results and keep improving the system after launch.', 'gear'],
];

/* Trust — the four pillars we work to. Short line for cards (Home), concrete
   practices for the detail section (Services). Only claim what our products
   and projects actually do; "aligned with" — not "certified" — for regulations. */
export const TRUST: [string, string, IconName][] = [
  [
    'Security',
    'Least-privilege access, encrypted secrets and a security scan on every change.',
    'shield',
  ],
  ['Governance', 'A person approves what AI does — clear roles, limits and hand-offs.', 'consult'],
  ['Audit', 'A traceable record of every task, change and admin action.', 'eye'],
  ['Compliance', 'Built to align with GDPR and the EU AI Act, privacy by design.', 'check'],
];

export const TRUST_PRACTICES: Record<string, string[]> = {
  Security: [
    'Every code change is scanned for vulnerabilities and leaked secrets before it ships',
    'Least-privilege access — people and AI agents reach only the systems they’re allowed to',
    'API keys and credentials encrypted at rest',
    'AI agents limited to approved tools and data — they don’t make things up or act outside their remit',
  ],
  Governance: [
    'A person approves the AI’s plan before work starts, and reviews the result before release',
    'Role-based access levels and per-person AI budgets set by your admins',
    'AI hands over to a person whenever a request needs human judgement',
  ],
  Audit: [
    'Every task keeps its plan, changes and review outcome in one place',
    'Access changes are logged with who made them and when',
    'Voice-agent calls can be recorded, transcribed and classified for review',
  ],
  Compliance: [
    'Built to align with GDPR and the EU AI Act (Regulation 2024/1689)',
    'Data processed in the EU for European deployments',
    'Privacy by design — we collect only the data a workflow needs',
  ],
};
